
import { computed, defineComponent, PropType, ref, shallowRef, watch, provide } from 'vue';
import { useI18n } from 'vue-i18n';
import Sidebar from 'primevue/sidebar';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import TabMenu from 'primevue/tabmenu';
import FlowOperatorForm from './FlowOperatorForm.vue';
import type { FlowOperatorMetadata } from '@/types/flow-types';
import type { FlowStructureDto } from '@/types/flow-structure';
import {
  getCompatibleOperators,
  getIncompatibleEventValues,
  getOperatorById,
  groupOperatorsByKind,
  OperatorInsertContext,
} from './operators/registry';
import { parseOperatorParameters } from '@/helpers/flowParameters';
import { useServiceVerification } from '@/composables/flows/useServiceVerification';
import { getFeatureFlagForOperator } from '@/configs/feature-flags';
import ComingSoonBadge from '@/components/shared/ComingSoonBadge.vue';

type PanelMode = 'list' | 'form';

export default defineComponent({
  name: 'FlowOperatorPanel',

  components: {
    Sidebar,
    Button,
    InputText,
    TabMenu,
    FlowOperatorForm,
    ComingSoonBadge,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    context: {
      type: Object as PropType<OperatorInsertContext | null>,
      default: null,
    },
    editStep: {
      type: Object as PropType<{ id: string; type: string; parameters: Record<string, any> } | null>,
      default: null,
    },
    replaceStep: {
      type: Object as PropType<{ id: string; type: string } | null>,
      default: null,
    },
    flow: {
      type: Object as PropType<FlowStructureDto | null>,
      default: null,
    },
  },

  emits: ['update:visible', 'add', 'save', 'replace', 'decrease-branches', 'update-step'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Provide flow and context to child components (for LinkedOperatorSelector)
    const flowRef = computed(() => props.flow);
    const contextRef = computed(() => props.context);
    provide('flow', flowRef);
    provide('insertContext', contextRef);
    // Events incompatible as a child of the insertion parent — the internal-event
    // form hides them from its event picker (clean UX vs. post-hoc warning).
    const forbiddenEventValues = computed(() => getIncompatibleEventValues(props.context));
    provide('forbiddenEventValues', forbiddenEventValues);

    // State for hiding panel during selection mode (without losing form state)
    const isHiddenForSelection = ref(false);

    // Provide reopen panel callback for LinkedOperatorSelector
    const reopenPanel = () => {
      isHiddenForSelection.value = false;
    };
    provide('reopenPanel', reopenPanel);

    // Handle start-selection event from LinkedOperatorSelector
    const handleStartSelection = () => {
      // Hide the panel visually when entering selection mode (but keep state)
      isHiddenForSelection.value = true;
    };
    provide('handleStartSelection', handleStartSelection);

    // Provide updateStep callback so composables can persist data without user clicking save
    const updateStep = (payload: { stepId: string; data: Record<string, any> }) => {
      emit('update-step', payload);
    };
    provide('updateStep', updateStep);
    const searchQuery = ref('');
    const activeTab = ref(0);
    const mode = ref<PanelMode>('list');
    const selectedOperator = shallowRef<FlowOperatorMetadata | null>(null);
    const formData = ref<any>(null);

    const compatibleOperators = computed(() => {
      if (!props.context) return [];
      return getCompatibleOperators(props.context);
    });

    const groupedOperators = computed(() => {
      return groupOperatorsByKind(compatibleOperators.value);
    });

    const filterBySearch = (operators: FlowOperatorMetadata[]) => {
      if (!searchQuery.value.trim()) return operators;
      const query = searchQuery.value.toLowerCase();
      return operators.filter(op => {
        const label = t(op.label).toLowerCase();
        return label.includes(query);
      });
    };

    const filteredTriggers = computed(() => filterBySearch(groupedOperators.value.triggers));
    const filteredActions = computed(() => filterBySearch(groupedOperators.value.actions));
    const filteredFilters = computed(() => filterBySearch(groupedOperators.value.filters));

    const filteredOperators = computed(() => [
      ...filteredTriggers.value,
      ...filteredActions.value,
      ...filteredFilters.value,
    ]);

    const tabItems = computed(() => {
      const items: { label: string; key: string }[] = [];
      if (groupedOperators.value.triggers.length > 0) {
        items.push({ label: t('flows.operatorKinds.trigger'), key: 'triggers' });
      }
      if (groupedOperators.value.actions.length > 0) {
        items.push({ label: t('flows.operatorKinds.action'), key: 'actions' });
      }
      if (groupedOperators.value.filters.length > 0) {
        items.push({ label: t('flows.operatorKinds.filter'), key: 'filters' });
      }
      return items;
    });

    const currentOperators = computed(() => {
      const key = tabItems.value[activeTab.value]?.key;
      if (key === 'triggers') return filteredTriggers.value;
      if (key === 'actions') return filteredActions.value;
      if (key === 'filters') return filteredFilters.value;
      return [];
    });

    // Service verification
    const { verifyAndShowModalIfNeeded } = useServiceVerification();

    const openOperatorForm = (operator: FlowOperatorMetadata) => {
      selectedOperator.value = operator;
      formData.value = operator.defaultData(undefined, props.context?.campaignType);
      mode.value = 'form';
    };

    const selectOperator = async (operator: FlowOperatorMetadata) => {
      // Verify services before opening the form
      const canProceed = await verifyAndShowModalIfNeeded(
        operator.id,
        () => openOperatorForm(operator),
      );

      if (canProceed) {
        openOperatorForm(operator);
      }
    };

    const backToList = () => {
      // In edit mode, close the panel instead of going back to list
      if (isEditMode.value) {
        emit('update:visible', false);
        return;
      }
      mode.value = 'list';
      selectedOperator.value = null;
      formData.value = null;
    };

    const handleClose = () => {
      emit('update:visible', false);
    };

    const isEditMode = computed(() => !!props.editStep);
    const isReplaceMode = computed(() => !!props.replaceStep);

    const confirmAdd = (data?: any) => {
      if (selectedOperator.value) {
        let finalData = data ?? formData.value;

        // Handle branch decrease in edit mode before cleaning data
        if (isEditMode.value && props.editStep) {
          const initialOutputs = finalData.initial_number_outputs;
          const outputsToDelete = finalData.outputs_to_delete;
          const newOutputs = selectedOperator.value.outputs?.(finalData) ?? 1;

          console.log('[FlowOperatorPanel] Branch decrease check:', {
            initialOutputs,
            newOutputs,
            outputsToDelete,
            finalData,
          });

          // For ABTest: use outputs_to_delete array
          if (outputsToDelete && Array.isArray(outputsToDelete) && outputsToDelete.length > 0) {
            console.log('[FlowOperatorPanel] Emitting decrease-branches for ABTest:', outputsToDelete);
            emit('decrease-branches', {
              stepId: props.editStep.id,
              outputsToRemove: outputsToDelete,
            });
          }
          // For filters: if outputs decreased from 2 to 1, remove output_2
          else if (initialOutputs !== undefined && initialOutputs !== null && newOutputs < initialOutputs) {
            console.log('[FlowOperatorPanel] Emitting decrease-branches for filter');
            emit('decrease-branches', {
              stepId: props.editStep.id,
              outputsToRemove: ['output_2'],
            });
          }
        }

        // Call beforeInsert if defined to transform data before saving
        if (selectedOperator.value.beforeInsert) {
          finalData = selectedOperator.value.beforeInsert(finalData);
        }

        if (isEditMode.value && props.editStep) {
          emit('save', {
            stepId: props.editStep.id,
            data: finalData,
          });
        } else if (isReplaceMode.value && props.replaceStep) {
          emit('replace', {
            operator: selectedOperator.value,
            data: finalData,
          });
        } else {
          emit('add', {
            operator: selectedOperator.value,
            data: finalData,
          });
        }
        emit('update:visible', false);
      }
    };


    watch([() => props.visible, () => props.editStep, () => props.replaceStep, () => props.context], ([newVisible, newEditStep, newReplaceStep, newContext]) => {
      if (newVisible) {
        if (newEditStep) {
          // Edit mode: open form directly with existing data
          const operator = getOperatorById(newEditStep.type);
          if (operator) {
            selectedOperator.value = operator;
            // Parse parameters back to form data format
            const parsedParams = parseOperatorParameters(newEditStep.parameters);
            const baseData = { ...operator.defaultData(undefined, props.context?.campaignType), ...parsedParams };

            // Store initial number of outputs for tracking changes
            if (operator.outputs) {
              baseData.initial_number_outputs = operator.outputs(baseData);
            }

            formData.value = baseData;
            mode.value = 'form';
          }
        } else if (newReplaceStep) {
          // Replace mode: show operator list to select replacement
          searchQuery.value = '';
          activeTab.value = 0;
          mode.value = 'list';
          selectedOperator.value = null;
          formData.value = null;
        } else if (newContext?.preSelectedOperator) {
          // Pre-selected operator: go directly to form (for bulk campaigns empty state)
          selectedOperator.value = newContext.preSelectedOperator;
          formData.value = newContext.preSelectedOperator.defaultData(undefined, newContext.campaignType);
          mode.value = 'form';
        } else {
          // Add mode: show operator list
          searchQuery.value = '';
          activeTab.value = 0;
          mode.value = 'list';
          selectedOperator.value = null;
          formData.value = null;
        }
      }
    }, { immediate: true });

    const getOperatorFlag = getFeatureFlagForOperator;

    return {
      t,
      searchQuery,
      getOperatorFlag,
      activeTab,
      mode,
      selectedOperator,
      formData,
      groupedOperators,
      filteredTriggers,
      filteredActions,
      filteredFilters,
      filteredOperators,
      tabItems,
      currentOperators,
      selectOperator,
      backToList,
      handleClose,
      confirmAdd,
      isEditMode,
      isReplaceMode,
      isHiddenForSelection,
    };
  },
});
